import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, Crown, Layers, Camera } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { useLang, BRANDS } from "@/lib/i18n";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/shop/ProductCard";
import { TractorPicker } from "@/components/shop/TractorPicker";

const HERO = "https://static.prod-images.emergentagent.com/jobs/f8dd797a-f629-4bbc-b98a-8b3f0c898da5/images/83d70cfa195f7365abfe2ea68780608abc452c805002313d8e3213f95f85bbb3.jpeg";
const BRAND_KEY = { Grammer: "brand_gr", Proboss: "brand_pb", "Total Source": "brand_ts" };

const Stat = ({ icon: Icon, value, label }) => (
  <div className="border border-[#3B4157] bg-[#292D3C]/80 backdrop-blur p-4">
    <Icon className="w-4 h-4 text-orange-500" />
    <div className="font-display font-black text-xl sm:text-3xl mt-2">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-x">{label}</div>
  </div>
);

const Hero = ({ stock }) => {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden border-b border-[#3B4157]">
      <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#1F2230] via-[#1F2230]/85 to-transparent" />
      <div className="absolute inset-0 grid-lines" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-16 pb-10 sm:pt-24 sm:pb-12 lg:pt-28 lg:pb-14 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="rise text-xs font-mono-x uppercase tracking-[0.25em] text-orange-500">// {t("hero_kicker")}</div>
          <h1 className="rise font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-5" style={{ animationDelay: "80ms" }}>
            {t("hero_title_1")}<br /><span className="text-orange-500">{t("hero_title_2")}</span>
          </h1>
          <p className="rise text-slate-300 text-base mt-6 max-w-xl leading-relaxed" style={{ animationDelay: "160ms" }}>{t("hero_sub")}</p>
          <div className="rise flex flex-wrap gap-3 mt-10" style={{ animationDelay: "240ms" }}>
            <Link to="/catalog" data-testid="hero-catalog-btn" className="group inline-flex items-center gap-2 h-12 px-6 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 transition-colors">
              {t("hero_cta")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <TractorPicker />
        </div>
        <div className="lg:col-span-4 lg:col-start-9 self-end grid grid-cols-3 lg:grid-cols-1 gap-3 rise" style={{ animationDelay: "320ms" }}>
          <Stat icon={Layers} value={stock} label={t("stat_stock")} />
          <Stat icon={Truck} value={`1–3 ${t("days")}`} label={t("stat_delivery")} />
          <Stat icon={Crown} value="Premium" label={t("stat_warranty")} />
        </div>
      </div>
    </section>
  );
};

const Brands = () => {
  const { t } = useLang();
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-12">
      <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight">{t("brands_title")}</h2>
      <div className="grid md:grid-cols-3 gap-px bg-[#3B4157] border border-[#3B4157] mt-8">
        {BRANDS.map((b) => (
          <Link key={b} to={`/sedynes/${b.toLowerCase().replace(" ", "-")}`} data-testid={`brand-tile-${b.toLowerCase().replace(" ", "-")}`}
            className="group bg-[#292D3C] p-8 hover:bg-[#30354A] transition-colors">
            <div className="font-display font-black uppercase text-4xl tracking-tight group-hover:text-orange-500 transition-colors">{b}</div>
            <p className="text-sm text-slate-400 mt-3 min-h-10">{t(BRAND_KEY[b])}</p>
            <ArrowRight className="w-5 h-5 mt-6 text-slate-600 group-hover:text-orange-500 group-hover:translate-x-1 transition-transform" />
          </Link>
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { sort: "popular" } }).then((r) => {
      const promo = (p) => p.old_price > p.price || !!p.gift_lt;
      const promos = r.data.filter(promo);
      setProducts([...promos, ...r.data.filter((p) => !promo(p))].slice(0, Math.max(4, Math.min(8, promos.length))));
    });
  }, []);
  useSeo({ title: t("seo_home_title"), description: t("seo_home_desc"), jsonLd: {
    "@context": "https://schema.org", "@type": "Store", name: "Sėdynės.lt", alternateName: ["Sedynes.lt", "Sėdynių centras"], url: window.location.origin,
    telephone: "+37064284410", email: "prekyba@mtech.lt", address: { "@type": "PostalAddress", addressCountry: "LT" } } });
  const [stock, setStock] = useState(0);
  useEffect(() => { api.get("/products/count").then((r) => setStock(r.data.variants)); }, []);

  return (
    <div data-testid="home-page">
      <Hero stock={stock} />
      <Brands />
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-12">
        <div className="border border-orange-500/40 bg-[#292D3C] p-8 sm:p-10 grid md:grid-cols-[auto_1fr_auto] gap-6 items-center">
          <span className="grid place-items-center w-14 h-14 bg-orange-500 text-[#1F2230]"><Camera className="w-7 h-7" /></span>
          <div>
            <h2 className="font-display font-black uppercase text-2xl sm:text-3xl tracking-tight">{t("finder_title")}</h2>
            <p className="text-sm text-slate-400 mt-2 max-w-2xl">{t("finder_cta_sub")}</p>
          </div>
          <Link to="/seat-finder" data-testid="home-finder-btn" className="group inline-flex items-center gap-2 h-12 px-6 bg-orange-500 text-[#1F2230] font-display font-bold uppercase tracking-widest hover:bg-orange-400 transition-colors">
            {t("finder_cta")} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pt-12">
        <div className="flex items-end justify-between">
          <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight">{t("featured")}</h2>
          <Link to="/catalog" data-testid="home-view-all-link" className="text-sm uppercase tracking-widest text-orange-500 hover:text-orange-400 font-display font-semibold">{t("view_all")} →</Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {products.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
        </div>
      </section>
      <section data-testid="home-seo-text" className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
        <div className="max-w-3xl border-l-2 border-orange-500/60 pl-6">
          <h2 className="font-display font-bold uppercase text-base md:text-lg tracking-tight">{t("seo_h2")}</h2>
          <p className="text-sm text-slate-400 mt-4 leading-relaxed">{t("seo_p1")}</p>
          <p className="text-sm text-slate-400 mt-3 leading-relaxed">{t("seo_p2")}</p>
        </div>
      </section>
    </div>
  );
}
